import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import useUnsavedWarning from '../../../hooks/useUnsavedWarning';
import Button from "../../../components/Button";
import TextInput from "../../../components/TextInput";
import { Trash2 } from "lucide-react";
import { getAbout, saveAbout, apiUrl } from "../../../api";
import PageLoader from "../../../components/PageLoader";
import Upload from "../../../components/Upload";
import Swal from "sweetalert2";

const AdminCMSAbout = () => {
  const [form, setForm] = useState({
    heroTitle: "",
    heroSubtitle: "",
    quoteText: "",
    missionTitle: "",
    missionDesc: "",
    visionTitle: "",
    visionDesc: "",
    promiseTitle: "",
    promiseDesc: "",
    whyUs: [],
    stats: [],
    demoVideoUrl: "",
    demoVideoTitle: "Watch Demo",
    demoVideoDesc: "",
  });
  const [originalForm, setOriginalForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isFormInvalid = useMemo(() => {
    // Basic fields
    const fields = [
      "heroTitle",
      "heroSubtitle",
      "quoteText",
      "missionTitle",
      "missionDesc",
      "visionTitle",
      "visionDesc",
      "promiseTitle",
      "promiseDesc",
    ];
    if (fields.some((f) => !form[f]?.trim())) return true;

    // whyUs items
    if (form.whyUs.length === 0) return true;
    if (form.whyUs.some((item) => !item.title?.trim() || !item.text?.trim()))
      return true;

    // stats items
    if (form.stats.length === 0) return true;
    if (form.stats.some((item) => !item.value?.trim() || !item.label?.trim()))
      return true;

    return false;
  }, [form]);

  const isChanged = useMemo(() => {
    if (!originalForm) return false;
    return JSON.stringify(form) !== JSON.stringify(originalForm);
  }, [form, originalForm]);
  useUnsavedWarning(isChanged);

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const stored = await getAbout();

        if (!mounted) return;

        if (stored && typeof stored === "object") {
          const data = {
            heroTitle: stored.heroTitle || "",
            heroSubtitle: stored.heroSubtitle || "",
            quoteText: stored.quoteText || "",
            missionTitle: stored.missionTitle || "",
            missionDesc: stored.missionDesc || "",
            visionTitle: stored.visionTitle || "",
            visionDesc: stored.visionDesc || "",
            promiseTitle: stored.promiseTitle || "",
            promiseDesc: stored.promiseDesc || "",
            whyUs: Array.isArray(stored.whyUs)
              ? stored.whyUs.map((w) => ({
                  title: w.title || "",
                  text: w.text || "",
                }))
              : [],
            stats: Array.isArray(stored.stats)
              ? stored.stats.map((s) => ({
                  value: s.value || "",
                  label: s.label || "",
                }))
              : [],
            demoVideoUrl: stored.demoVideoUrl || "",
            demoVideoTitle: stored.demoVideoTitle || "Watch Demo",
            demoVideoDesc: stored.demoVideoDesc || "",
          };
          setForm(data);
          setOriginalForm(data);
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Data not available",
            confirmButtonColor: "#38915c",
          });
        }
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            err.response?.data?.message ||
            err.message ||
            "Failed to load About content",
          confirmButtonColor: "#38915c",
        });
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const update = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setTouched((prev) => ({ ...prev, [field]: true }));
    if (!value?.trim()) {
      setErrors((prev) => ({ ...prev, [field]: "This field is required" }));
    } else {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const updateWhyUs = (index, field, value) => {
    setForm((f) => {
      const list = [...(f.whyUs || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...f, whyUs: list };
    });
  };

  const addWhyUs = () => {
    setForm((f) => ({
      ...f,
      whyUs: [...(f.whyUs || []), { title: "", text: "" }],
    }));
    Swal.fire({
      icon: "success",
      title: "Item Added",
      text: "New Why Us item added to editor.",
      timer: 1500,
      showConfirmButton: false,
    });
  };

  const removeWhyUs = async (index) => {
    const confirmed = await Swal.fire({
      title: "Remove item?",
      text: "This item will be removed from the editor.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove it!",
    });

    if (confirmed.isConfirmed) {
      setForm((f) => ({
        ...f,
        whyUs: (f.whyUs || []).filter((_, i) => i !== index),
      }));
      Swal.fire({
        icon: "success",
        title: "Removed",
        text: "Item removed. Remember to save changes.",
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };

  const updateStats = (index, field, value) => {
    setForm((f) => {
      const list = [...(f.stats || [])];
      list[index] = { ...list[index], [field]: value };
      return { ...f, stats: list };
    });
  };

  const addStat = () => {
    setForm((f) => ({
      ...f,
      stats: [...(f.stats || []), { value: "", label: "" }],
    }));
    Swal.fire({
      icon: "success",
      title: "Stat Added",
      text: "New stat added to editor.",
      timer: 1500,
      showConfirmButton: false,
    });
  };

  const removeStat = async (index) => {
    const confirmed = await Swal.fire({
      title: "Remove stat?",
      text: "This stat will be removed from the editor.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove it!",
    });

    if (confirmed.isConfirmed) {
      setForm((f) => ({
        ...f,
        stats: (f.stats || []).filter((_, i) => i !== index),
      }));
      Swal.fire({
        icon: "success",
        title: "Removed",
        text: "Stat removed. Remember to save changes.",
        timer: 1500,
        showConfirmButton: false,
      });
    }
  };

  const handleSave = async () => {
    if (isFormInvalid) {
      Swal.fire({
        icon: "warning",
        title: "Incomplete Fields",
        text: "Please fill in all required fields before saving.",
        confirmButtonColor: "#38915c",
      });
      return;
    }

    setSaving(true);
    try {
      const saved = await saveAbout(form);
      if (saved && typeof saved === "object") {
        const data = {
          heroTitle: saved.heroTitle || "",
          heroSubtitle: saved.heroSubtitle || "",
          quoteText: saved.quoteText || "",
          missionTitle: saved.missionTitle || "",
          missionDesc: saved.missionDesc || "",
          visionTitle: saved.visionTitle || "",
          visionDesc: saved.visionDesc || "",
          promiseTitle: saved.promiseTitle || "",
          promiseDesc: saved.promiseDesc || "",
          whyUs: Array.isArray(saved.whyUs) ? saved.whyUs : [],
          stats: Array.isArray(saved.stats) ? saved.stats : [],
          demoVideoUrl: saved.demoVideoUrl || "",
          demoVideoTitle: saved.demoVideoTitle || "Watch Demo",
          demoVideoDesc: saved.demoVideoDesc || "",
        };
        setForm(data);
        setOriginalForm(data);
        setErrors({});
        setTouched({});
        Swal.fire({
          icon: "success",
          title: "Saved",
          text: "About Us content updated successfully",
          confirmButtonColor: "#38915c",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          err.response?.data?.message ||
          err.message ||
          "Failed to save About content",
        confirmButtonColor: "#38915c",
      });
    } finally {
      setSaving(false);
    }
  };

  // ✅ Loading State
  if (loading) {
    return <PageLoader />;
  }

  // ✅ No Data State
  if (!form) {
    return (
      <div className="text-center py-10 text-gray-500">Data not available</div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <Link
            to="/admin/cms"
            className="text-sm text-primary-600 hover:underline mb-1 inline-block"
          >
            ← CMS Pages
          </Link>
          <h1 className="text-2xl font-bold text-gray-800">About Us</h1>
          <p className="text-gray-600 text-sm mt-1">
            Manage your company story and details. All fields are required.
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={saving || !isChanged || isFormInvalid}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="space-y-8 max-w-2xl">
        {/* Hero */}
        <section className="p-6 rounded-xl border-2 border-gray-200 bg-white">
          <h2 className="font-semibold text-gray-800 mb-4">Hero</h2>
          <div className="space-y-4">
            <TextInput
              label="Hero title"
              value={form.heroTitle}
              onChange={(e) => update("heroTitle", e.target.value)}
              error={touched.heroTitle && errors.heroTitle}
              required
            />
            <TextInput
              label="Hero subtitle"
              value={form.heroSubtitle}
              onChange={(e) => update("heroSubtitle", e.target.value)}
              error={touched.heroSubtitle && errors.heroSubtitle}
              multiline
              rows={2}
              required
            />
          </div>
        </section>

        {/* Quote */}
        <section className="p-6 rounded-xl border-2 border-gray-200 bg-white">
          <h2 className="font-semibold text-gray-800 mb-4">Quote / Story</h2>
          <TextInput
            label="Quote text"
            value={form.quoteText}
            onChange={(e) => update("quoteText", e.target.value)}
            error={touched.quoteText && errors.quoteText}
            multiline
            rows={3}
            required
          />
        </section>

        {/* Mission Vision */}
        <section className="p-6 rounded-xl border-2 border-gray-200 bg-white">
          <h2 className="font-semibold text-gray-800 mb-4">
            Mission, Vision, Promise
          </h2>
          <div className="space-y-4">
            <TextInput
              label="Mission title"
              value={form.missionTitle}
              onChange={(e) => update("missionTitle", e.target.value)}
              error={touched.missionTitle && errors.missionTitle}
              required
            />
            <TextInput
              label="Mission description"
              value={form.missionDesc}
              onChange={(e) => update("missionDesc", e.target.value)}
              error={touched.missionDesc && errors.missionDesc}
              multiline
              rows={2}
              required
            />
            <TextInput
              label="Vision title"
              value={form.visionTitle}
              onChange={(e) => update("visionTitle", e.target.value)}
              error={touched.visionTitle && errors.visionTitle}
              required
            />
            <TextInput
              label="Vision description"
              value={form.visionDesc}
              onChange={(e) => update("visionDesc", e.target.value)}
              error={touched.visionDesc && errors.visionDesc}
              multiline
              rows={2}
              required
            />
            <TextInput
              label="Promise title"
              value={form.promiseTitle}
              onChange={(e) => update("promiseTitle", e.target.value)}
              error={touched.promiseTitle && errors.promiseTitle}
              required
            />
            <TextInput
              label="Promise description"
              value={form.promiseDesc}
              onChange={(e) => update("promiseDesc", e.target.value)}
              error={touched.promiseDesc && errors.promiseDesc}
              multiline
              rows={2}
              required
            />
          </div>
        </section>

        {/* Why Us */}
        <section className="p-6 rounded-xl border-2 border-gray-200 bg-white">
          <h2 className="font-semibold text-gray-800 mb-4">
            Why Market Mavericks?
          </h2>

          {form.whyUs.length === 0 && (
            <p className="text-gray-500 text-sm">No items available</p>
          )}

          {form.whyUs.map((item, i) => (
            <div key={i} className="flex gap-2 mb-3 p-3 bg-gray-50 rounded-lg">
              <TextInput
                label="Title"
                value={item.title}
                onChange={(e) => updateWhyUs(i, "title", e.target.value)}
                error={!item.title?.trim() && "Required"}
                required
              />
              <TextInput
                label="Text"
                value={item.text}
                onChange={(e) => updateWhyUs(i, "text", e.target.value)}
                error={!item.text?.trim() && "Required"}
                required
              />
              <button
                onClick={() => removeWhyUs(i)}
                className="p-2 text-danger-600 pt-8"
                title="Remove"
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))}

          <button onClick={addWhyUs} className="text-sm text-primary-600">
            + Add item
          </button>
        </section>

        {/* Stats */}
        <section className="p-6 rounded-xl border-2 border-gray-200 bg-white">
          <h2 className="font-semibold text-gray-800 mb-4">Stats strip</h2>

          {form.stats.length === 0 && (
            <p className="text-gray-500 text-sm">No stats available</p>
          )}

          {form.stats.map((item, i) => (
            <div key={i} className="flex gap-2 mb-3 items-end">
              <TextInput
                label="Value"
                value={item.value}
                onChange={(e) => updateStats(i, "value", e.target.value)}
                error={!item.value?.trim() && "Required"}
                required
              />
              <TextInput
                label="Label"
                value={item.label}
                onChange={(e) => updateStats(i, "label", e.target.value)}
                error={!item.label?.trim() && "Required"}
                required
              />
              <button
                onClick={() => removeStat(i)}
                className="p-2 text-danger-600 mb-2"
                title="Remove"
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))}

          <button onClick={addStat} className="text-sm text-primary-600">
            + Add stat
          </button>
        </section>

        {/* Demo Video Section */}
        <section className="p-6 rounded-xl border-2 border-gray-200 bg-white">
          <h2 className="font-semibold text-gray-800 mb-4">Demo Video</h2>
          <div className="space-y-4">
            <Upload
              type="video"
              label="Demo Video"
              value={form.demoVideoUrl}
              onChange={(url) => update("demoVideoUrl", url)}
              uploadEndpoint={apiUrl("/api/uploads/video")}
              required
            />
            <TextInput
              label="Button Text"
              value={form.demoVideoTitle}
              onChange={(e) => update("demoVideoTitle", e.target.value)}
              placeholder="e.g. Watch Demo"
            />
            <TextInput
              label="Short Description (Optional)"
              value={form.demoVideoDesc}
              onChange={(e) => update("demoVideoDesc", e.target.value)}
              multiline
              rows={2}
              placeholder="Tell users what they'll see in the demo..."
            />
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminCMSAbout;
